import { AvatarDropdown, AvatarName, SelectLang } from "@/components";
import { currentUser as queryCurrentUser } from "@/services/ant-design-pro/api";
import { LinkOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { PageLoading, SettingDrawer } from "@ant-design/pro-components";
import "@ant-design/v5-patch-for-react-19";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history } from "@umijs/max";
import defaultSettings from "../config/defaultSettings";
import { errorConfig } from "./requestErrorConfig";

const isDev = process.env.NODE_ENV === "development";
const isDevOrTest = isDev || process.env.CI;
const loginPath = "/user/login";

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg: any = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data[0];
    } catch (_error) {
      history.push(loginPath);
      localStorage.removeItem("access_token");
      localStorage.removeItem("rid");
      localStorage.removeItem("login_method");
      localStorage.removeItem("form_user");
    }
    return undefined;
  };

  const accessToken = localStorage.getItem("access_token");
  const loginMethod = localStorage.getItem("login_method");
  
  if (accessToken) {
    let currentUser;
    
    if (loginMethod === "form") {
      // For form login, try to get user from localStorage or create mock user
      const storedUser = localStorage.getItem("form_user");
      if (storedUser) {
        try {
          currentUser = JSON.parse(storedUser);
        } catch {
          // If parsing fails, create a basic user
          currentUser = {
            name: "Form User",
            email: "user@example.com",
            access: "user",
          } as API.CurrentUser;
        }
      } else {
        // Create a basic user if no stored user found
        currentUser = {
          name: "Form User", 
          email: "user@example.com",
          access: "user",
        } as API.CurrentUser;
      }
    } else {
      // For SSO login, fetch user info from service
      currentUser = await fetchUserInfo();
    }
    
    return {
      fetchUserInfo,
      loading: true,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  if (initialState?.loading && initialState?.currentUser) {
    setTimeout(() => {
      setInitialState((s) => ({
        ...s,
        loading: false,
      }));
    }, 0);
  }

  return {
    actionsRender: () => [
      // <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => (
        <AvatarDropdown>{avatarChildren}</AvatarDropdown>
      ),
    },
    waterMarkProps: {
      // content: initialState?.currentUser?.name,
      content: undefined,
    },
    // footerRender: () => <Footer />,
    footerRender: () => null,
    onPageChange: () => {
      const { location } = history;

      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    links:
      isDevOrTest || true
        ? [
            <a
              key="openapi"
              href="https://notification-dev.scc.fpt.vn/backend/v1/docs#/"
              target="_blank"
            >
              <LinkOutlined />
              <span>OpenAPI</span>
            </a>,
          ]
        : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      if (initialState?.loading) return <PageLoading />;
      const isInIframe = window.self !== window.top;
      return (
        <>
          {children}
          {(true || isDevOrTest) && !isInIframe && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: isDev ? "" : "https://proapi.azurewebsites.net",
  ...errorConfig,
};
